// Phase 2Z.p — Relevant-week Dynamic Rotation helper (Option B).
//
// Pure JS, deterministic. Takes the Rota Style cells for ONE
// employee + the cycle-week being generated + the rotation step,
// and returns a new (in-memory) array of cells where the
// assignments have been rotated WITHIN THAT WEEK ONLY.
//
// Strict invariants enforced by source-level + Node-executed locks
// in `test_phase_2zp_dynamic_rotation_relevant_week.py`:
//
//   * Rotation is bounded to a single cycle-week — Week 1 cells
//     can never inherit Week 2's assignments, and vice-versa.
//   * Active cells only — cells with no assignments stay empty.
//   * Weekend cells participate ONLY if they already exist (i.e.
//     they're active for this employee in this week). The helper
//     never invents a Saturday/Sunday cell.
//   * Times / breaks / employee_id / cycle_day_index / cell_id all
//     stay UNCHANGED. The helper rotates assignment content only.
//   * Static profiles (dyn_shift === 0) → output identical to input
//     (byte-equal array, no allocation churn beyond a shallow
//     clone).
//   * Multi-cell-per-day slots → that day's cells stay static (no
//     rotation) to avoid ambiguity, matching the existing
//     engine's single-cell-only guard.
//   * Helper is pure: no React, no fetch, no DOM, no setCells, no
//     api.* — every input comes from in-memory state.

// Returns the [start, end) cycle-day-index range for a given
// `weekInCycle` (0-indexed). Multi-week cycles use 7-day weeks.
export function weekDayRange(weekInCycle) {
  const w = Math.max(0, Math.floor(weekInCycle));
  return { start: w * 7, end: w * 7 + 7 };
}

// Rotate ONE employee's cells within ONE cycle-week.
//
// Args:
//   cells         — array of {cell_id, employee_id, cycle_day_index,
//                   start_time, end_time, break_minutes,
//                   break_start_time, assignments, ...}. May contain
//                   cells from OTHER employees and OTHER weeks — the
//                   helper filters by employee_id + week.
//   employeeId    — string. The employee whose cells to rotate.
//   weekInCycle   — 0-indexed cycle-week to operate on.
//   dynShift      — integer rotation step (0 = static; >0 rotates
//                   forward).
//
// Returns: a NEW array containing every input cell. For the chosen
// employee + week, cells with exactly one assigned source slot in
// the same week have their `assignments` substituted from the
// rotated source cell. All OTHER fields (and all other cells) are
// returned unchanged.
export function rotateRelevantWeekCells({
  cells, employeeId, weekInCycle, dynShift,
}) {
  const out = (cells || []).map((c) => ({ ...c }));
  if (!employeeId) return out;
  const shift = Math.floor(Number(dynShift) || 0);
  if (shift === 0) return out;
  const { start, end } = weekDayRange(weekInCycle);

  // Bucket the employee's cells in THIS week by cycle_day_index.
  const cellsByDay = new Map();
  for (const c of out) {
    if (c.employee_id !== employeeId) continue;
    if (typeof c.cycle_day_index !== "number") continue;
    if (c.cycle_day_index < start || c.cycle_day_index >= end) continue;
    const list = cellsByDay.get(c.cycle_day_index) || [];
    list.push(c);
    cellsByDay.set(c.cycle_day_index, list);
  }

  // Active days = days within the week range that have at least one
  // cell with at least one assignment. Empty days stay empty —
  // they're NOT inserted into `activeDays`, so they can't receive
  // a rotated assignment.
  const activeDays = [];
  for (let d = start; d < end; d += 1) {
    const list = cellsByDay.get(d) || [];
    if (list.length === 0) continue;
    // Multi-cell-per-day → stays static (matches existing engine
    // guard). Still skipped from the active list so rotation
    // never reads from or writes to a multi-cell day.
    if (list.length !== 1) continue;
    const cell = list[0];
    const assigns = cell.assignments || [];
    if (assigns.length === 0) continue;
    activeDays.push(d);
  }

  const n = activeDays.length;
  if (n < 2) return out;  // 0 or 1 active day → nothing to rotate.

  // Effective shift modulo the count of ACTIVE days only.
  const k = ((shift % n) + n) % n;
  if (k === 0) return out;

  // Snapshot the source assignments BEFORE any mutation so a 1->2
  // rotation reads from the pre-rotation state.
  const sourceAssignsByDay = new Map();
  for (const d of activeDays) {
    const cell = cellsByDay.get(d)[0];
    sourceAssignsByDay.set(d, (cell.assignments || []).map((a) => ({ ...a })));
  }

  // For each active day d at index i in `activeDays`, substitute
  // the assignments from the day at index (i + k) % n.
  for (let i = 0; i < n; i += 1) {
    const dstDay = activeDays[i];
    const srcDay = activeDays[(i + k) % n];
    const dstCell = cellsByDay.get(dstDay)[0];
    dstCell.assignments = sourceAssignsByDay.get(srcDay);
  }

  return out;
}

// Build the per-row rotation summary used by the Preview drawer.
// Pure aggregation over the helper above. The drawer renders one
// row per (employee, week) pair. `rows` is keyed by employee_id.
export function buildRotationPreview({
  cells, employees, weekInCycle, dynShift,
}) {
  const rows = [];
  for (const e of (employees || [])) {
    const before = (cells || []).filter(
      (c) => c.employee_id === e.employee_id,
    );
    const after = rotateRelevantWeekCells({
      cells: before,
      employeeId: e.employee_id,
      weekInCycle,
      dynShift,
    });
    const { start, end } = weekDayRange(weekInCycle);
    const dayDeltas = [];
    for (let d = start; d < end; d += 1) {
      const bCell = before.find((c) => c.cycle_day_index === d);
      const aCell = after.find((c) => c.cycle_day_index === d);
      const bAssigns = (bCell?.assignments || []).map((a) => a.category_id);
      const aAssigns = (aCell?.assignments || []).map((a) => a.category_id);
      const changed = (
        bAssigns.length !== aAssigns.length
        || bAssigns.some((id, idx) => id !== aAssigns[idx])
      );
      dayDeltas.push({
        cycle_day_index: d,
        before: bAssigns,
        after: aAssigns,
        empty: !bCell,
        changed,
      });
    }
    rows.push({
      employee_id: e.employee_id,
      employee_name: e.name,
      team_id: e.team_id,
      dayDeltas,
      anyChange: dayDeltas.some((d) => d.changed),
    });
  }
  return { weekInCycle, dynShift, rows };
}
